import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { getValue } from "../../../../utils/object.utils";
import { UserAvatar, AvatarSize } from "../../../UserAvatar/UserAvatar";
import { Colors } from "../../../../Constants/colors";
import { RESULT_STATUS } from "../../../../Constants/Enums/Results.enum";
import { RESULT_STATUS_TITLE } from "../../../../Constants/Titles/result.title";
import {
  updateGradeInResultEntryForStudent,
  updateMarksInResultEntryForStudent,
  updateMetaDataForStudent,
  updateRemarksForStudent,
} from "../../../../Redux/Slices/studentResuilt.slice";
import { setErrorMessage } from "../../../../Redux/Slices/alert.slice";
import { EditableCell } from "./EditableCell";
import { RemarksCell } from "./RemarksCell";
import { GradeDropdownCell } from "./GradeDropdownCell";
import { DownloadIcon } from "../../../Icon/DownloadIcon";
import { isGradeOnlySubject, SubjectColumn } from "./subjectColumns";

interface Props {
  student: any;
  subjectColumns: SubjectColumn[];
  gradeOptions: string[];
  isDirty: boolean;
  isDownloading: boolean;
  onExport: (student: any) => unknown;
}

const getStatusColor = (status: RESULT_STATUS) => {
  switch (status) {
    case RESULT_STATUS.COMPLETED:
      return Colors.Success;
    case RESULT_STATUS.IN_PROGRESS:
      return Colors.TangerineOrange;
    case RESULT_STATUS.CANCELLED:
      return Colors.Tomato;
    default:
      return Colors.Grey;
  }
};

export const StudentResultRow = ({
  student,
  subjectColumns,
  gradeOptions,
  isDirty,
  isDownloading,
  onExport,
}: Props) => {
  const dispatch = useDispatch();
  const [marksErrors, setMarksErrors] = useState<Record<number, boolean>>({});

  const studentId = getValue(student, "student_id");
  const studentName = getValue(student, "full_name", "");
  const avatar = getValue(student, "avatar", "");
  const rollNo = getValue(student, "roll_no", "N/A");
  const percentage = getValue(student, "percentage", "N/A");
  const position = getValue(student, "position", "N/A");
  const resultStatus = getValue(
    student,
    "result_status",
    RESULT_STATUS.PENDING,
  );
  const metadata = getValue(student, "metadata", {}) as any;
  const link = getValue(student, "link", null);

  const entriesBySubjectId = new Map(
    getValue(student, "result_entries", []).map((entry: any) => [
      entry.subject_id,
      entry,
    ]),
  );

  const handleMarksCommit = (
    subjectId: number,
    fullMarks: number,
    raw: string,
  ) => {
    const marks = Number(raw);
    // Preserves the exact validity rule used previously in the grid.
    const isValid = !!marks && fullMarks >= marks && marks >= 0;

    if (!isValid) {
      setMarksErrors((prev) => ({ ...prev, [subjectId]: true }));
      dispatch(setErrorMessage("Marks can't be greater than full marks"));
      return;
    }

    setMarksErrors((prev) => ({ ...prev, [subjectId]: false }));
    dispatch(
      updateMarksInResultEntryForStudent({ studentId, subjectId, value: marks }),
    );
  };

  const handleGradeChange = (subjectId: number, value: string) => {
    dispatch(updateGradeInResultEntryForStudent({ studentId, subjectId, value }));
  };

  const handleMetadataCommit = (
    field: "height" | "weight" | "attendance",
    raw: string,
  ) => {
    dispatch(
      updateMetaDataForStudent({ studentId, field, value: Number(raw) || 0 }),
    );
  };

  const handleRemarksCommit = (raw: string) => {
    dispatch(updateRemarksForStudent({ studentId, value: raw }));
  };

  return (
    <tr className={isDirty ? "rs-row-dirty" : ""}>
      <td className="rs-sticky-col rs-student-cell">
        <div className="rs-student-info">
          <UserAvatar image={avatar} size={AvatarSize.xm} userName={studentName} />
          <div className="rs-student-text">
            <div className="rs-student-name">
              {studentName}
              {isDirty && <span className="rs-dirty-dot" title="Unsaved changes" />}
            </div>
            <div className="rs-student-meta">
              <span>Roll {rollNo}</span>
              <span
                className="rs-status-dot"
                style={{ backgroundColor: getStatusColor(resultStatus) }}
              />
              <span>{RESULT_STATUS_TITLE[resultStatus]}</span>
            </div>
          </div>
        </div>
      </td>

      {subjectColumns.map((column) => {
        const entry = entriesBySubjectId.get(column.subject_id) as any;
        const gradeOnly = isGradeOnlySubject(column.subject_type);

        return (
          <React.Fragment key={column.subject_id}>
            {!gradeOnly && (
              <td>
                <EditableCell
                  type="number"
                  value={entry ? entry.marks : ""}
                  disabled={!entry}
                  hasError={!!marksErrors[column.subject_id]}
                  placeholder={`/${column.full_marks}`}
                  onCommit={(raw) =>
                    handleMarksCommit(column.subject_id, column.full_marks, raw)
                  }
                />
              </td>
            )}
            <td>
              {gradeOnly ? (
                <GradeDropdownCell
                  value={entry ? entry.grade || "" : ""}
                  options={gradeOptions}
                  disabled={!entry}
                  onChange={(value) =>
                    handleGradeChange(column.subject_id, value)
                  }
                />
              ) : (
                <input
                  className="rs-cell-input"
                  value={entry ? entry.grade || "" : ""}
                  disabled
                  readOnly
                />
              )}
            </td>
          </React.Fragment>
        );
      })}

      <td className="rs-readonly-cell">{percentage}</td>
      <td className="rs-readonly-cell">{position}</td>

      <td>
        <EditableCell
          type="number"
          value={metadata?.attendance}
          onCommit={(raw) => handleMetadataCommit("attendance", raw)}
        />
      </td>
      <td>
        <EditableCell
          type="number"
          value={metadata?.height}
          onCommit={(raw) => handleMetadataCommit("height", raw)}
        />
      </td>
      <td>
        <EditableCell
          type="number"
          value={metadata?.weight}
          onCommit={(raw) => handleMetadataCommit("weight", raw)}
        />
      </td>
      <td className="rs-remarks-cell">
        <RemarksCell
          value={getValue(student, "remarks", "")}
          studentName={studentName}
          onCommit={handleRemarksCommit}
        />
      </td>
      <td className="rs-action-cell rs-sticky-col-right">
        <button
          type="button"
          className="rs-export-btn"
          title={link ? "Download report" : "Generate & download report"}
          disabled={isDownloading}
          onClick={() => onExport(student)}
        >
          {isDownloading ? (
            <span className="rs-spinner" />
          ) : (
            <DownloadIcon />
          )}
        </button>
      </td>
    </tr>
  );
};
